import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Plus, ListOrdered, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface PriceList {
  id: string;
  name: string;
  currencyCode: string;
  status: string;
  validFrom: string | null;
  validTo: string | null;
  notes: string | null;
  createdAt: string;
}

interface PriceListsResponse {
  priceLists: PriceList[];
  total: number;
}

export default function PriceListsPage() {
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newPl, setNewPl] = useState({ name: "", currencyCode: "USD", status: "active" });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<PriceListsResponse>({
    queryKey: ["/api/price-lists", { search: searchValue, page: currentPage, limit: pageSize }],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(pageSize),
        ...(searchValue && { search: searchValue }),
      });
      return api.get<PriceListsResponse>(`/price-lists?${params}`, sessionId);
    },
  });

  const createMutation = useMutation({
    mutationFn: (payload: typeof newPl) => api.post<PriceList>("/price-lists", payload, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/price-lists"] });
      setDialogOpen(false);
      setNewPl({ name: "", currencyCode: "USD", status: "active" });
      toast({ title: "Price list created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const columns = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      cell: (row: PriceList) => <div className="font-medium" data-testid={`text-pl-name-${row.id}`}>{row.name}</div>,
    },
    {
      key: "status",
      header: "Status",
      cell: (row: PriceList) => <StatusBadge status={row.status || "active"} type="account" />,
    },
    {
      key: "validFrom",
      header: "Valid From",
      cell: (row: PriceList) => (
        <span className="text-muted-foreground">
          {row.validFrom ? new Date(row.validFrom).toLocaleDateString() : "-"}
        </span>
      ),
    },
    {
      key: "validTo",
      header: "Valid To",
      cell: (row: PriceList) => (
        <span className="text-muted-foreground">
          {row.validTo ? new Date(row.validTo).toLocaleDateString() : "-"}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Price Lists"
        actions={
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search price lists..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-64"
            />
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="button-new-price-list">
                  <Plus className="h-4 w-4 mr-1" /> New Price List
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Price List</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  <div>
                    <label className="text-sm font-medium">Name</label>
                    <Input
                      value={newPl.name}
                      onChange={(e) => setNewPl({ ...newPl, name: e.target.value })}
                      placeholder="Price list name"
                      data-testid="input-pl-name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Status</label>
                    <Select value={newPl.status} onValueChange={(v) => setNewPl({ ...newPl, status: v })}>
                      <SelectTrigger data-testid="select-pl-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="expired">Expired</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    className="w-full"
                    disabled={!newPl.name || createMutation.isPending}
                    onClick={() => createMutation.mutate(newPl)}
                    data-testid="button-create-pl"
                  >
                    {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Create Price List
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <DataTable
        data={data?.priceLists || []}
        columns={columns}
        isLoading={isLoading}
        onRowClick={(row) => setLocation(`/price-lists/${row.id}`)}
        emptyMessage="No price lists found"
        emptyIcon={<ListOrdered className="h-12 w-12 text-muted-foreground/50" />}
        pageSize={pageSize}
        totalCount={data?.total || 0}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}
